import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ansCasesHref } from '@/pages/ans/OverviewPage';
import { fetchAnsPenalties } from '@/services/ans';
import type { AnsLadderTable, AnswerOut, QuantileSummary } from '@/services/types';

const QUANTILES = ['p10', 'p25', 'p50', 'p75', 'p90'] as const;

/**
 * ANS fines: the tabulated amounts and the ladders per track and per object.
 *
 * RN 489 tabulates the fine per infraction, so the amounts cluster on a few
 * hundred values and every quantile is one of them. A ladder here is a set
 * of steps, and the page says so on every panel rather than drawing a curve.
 */
export function AnsPenaltiesPage() {
  const { t, i18n } = useTranslation(['ans', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ans', 'penalties'],
    queryFn: fetchAnsPenalties,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ans:penalties.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ans:penalties.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ans:penalties.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ans/export/penalties" label={t('ans:penalties.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-tabulation>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ans:penalties.tabulation.title')}
          </h2>
          <Answer answer={data.tabulation} help={t('ans:penalties.tabulation.help')}>
            {(tab) => (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  {t('ans:penalties.tabulation.summary', {
                    top: number.format(tab.top.length),
                    share: percent.format(tab.top_share),
                    distinct: number.format(tab.distinct_amounts),
                  })}
                </p>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">
                          {t('ans:penalties.tabulation.amount')}
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          {t('ans:penalties.tabulation.cases')}
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          {t('ans:penalties.tabulation.share')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tab.top.map((row) => (
                        <tr key={row.amount} className="border-t" data-amount={row.amount}>
                          <td className="px-3 py-2 tabular-nums text-foreground">
                            {currency.format(row.amount)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-foreground">
                            {number.format(row.cases)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {percent.format(row.share)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground" data-fine-kinds>
                  {t('ans:penalties.tabulation.kinds', {
                    fixed: number.format(tab.fixed_fines),
                    daily: number.format(tab.daily_fines),
                  })}
                  {' · '}
                  {Object.entries(tab.by_penalty_type)
                    .map(([type, n]) => `${type}: ${number.format(n)}`)
                    .join(' · ')}
                </p>
              </div>
            )}
          </Answer>
        </section>

        <section className="rounded-lg border bg-card p-4" data-corpus-ladders>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ans:penalties.corpus.title')}
          </h2>
          <Answer answer={data.corpus} help={t('ans:penalties.corpus.help')}>
            {(ladders) => (
              <LadderPair
                original={ladders.original}
                final={ladders.final}
                originalLabel={t('ans:penalties.corpus.original')}
                finalLabel={t('ans:penalties.corpus.final')}
              />
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="ans:penalties.guide" />
          </div>
        </section>
      </div>

      <LadderTablePanel answer={data.by_track} />
      <LadderTablePanel answer={data.by_object} />
    </div>
  );
}

function LadderPair({
  original,
  final,
  originalLabel,
  finalLabel,
}: {
  original: QuantileSummary | null;
  final: QuantileSummary | null;
  originalLabel: string;
  finalLabel: string;
}) {
  const { i18n } = useTranslation();
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium" />
            {QUANTILES.map((q) => (
              <th key={q} className="px-3 py-2 text-right font-medium">
                {q}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(
            [
              [originalLabel, original],
              [finalLabel, final],
            ] as const
          ).map(([label, summary]) => (
            <tr key={label} className="border-t" data-ladder={label}>
              <td className="px-3 py-2 text-foreground">{label}</td>
              {QUANTILES.map((q) => (
                <td key={q} className="px-3 py-2 text-right tabular-nums text-foreground">
                  {summary ? currency.format(summary.quantiles[q] ?? 0) : '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LadderTablePanel({ answer }: { answer: AnswerOut<AnsLadderTable> }) {
  const { t, i18n } = useTranslation(['ans']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const kind = answer.value?.kind ?? 'track';
  return (
    <section className="rounded-lg border bg-card p-4" data-ladder-table={kind}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(`ans:penalties.ladders.${kind}.title`)}
      </h2>
      <Answer answer={answer} help={t(`ans:penalties.ladders.${kind}.help`)}>
        {(table) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">
                      {t(`ans:penalties.ladders.${kind}.group`)}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:penalties.ladders.cases')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:penalties.ladders.originalMedian')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:penalties.ladders.originalP90')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:penalties.ladders.finalMedian')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:penalties.ladders.reduced')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row) => (
                    <tr key={row.group} className="border-t" data-group={row.group}>
                      <td className="px-3 py-2 text-foreground">
                        <Link
                          to={ansCasesHref(
                            kind === 'track' ? { track: row.group } : { object: row.group },
                          )}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {row.group}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.cases)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {row.original ? currency.format(row.original.quantiles.p50 ?? 0) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {row.original ? currency.format(row.original.quantiles.p90 ?? 0) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {row.final ? currency.format(row.final.quantiles.p50 ?? 0) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.reduced)}
                        <span className="ml-1 text-xs">
                          ({percent.format(row.reduced_share_of_reviewed)})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {table.suppressed > 0 ? (
              <p className="text-xs text-muted-foreground" data-suppressed>
                {t('ans:penalties.ladders.suppressed', { n: number.format(table.suppressed) })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}
